import axios from 'axios'
import {
  signIn,
  confirmSignIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  updateUserAttributes,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth'
import { CognitoIdentityProvider as CognitoIdentityServiceProvider } from '@aws-sdk/client-cognito-identity-provider'
import authConfig from '../../common/service/authConfig.js'
import { armIdpLogout } from './idpLogout.js'

// The user pool's password policy. 8 is Cognito's default, and the sign up form has always
// been rejected below it.
const PASSWORD_MINIMUM_LENGTH = 8
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Cognito's own logout endpoint, which is what actually ends the Hosted UI session. `logout_uri`
// must be one of the client's registered logout URLs, so it comes from the server rather than being
// assembled here. Exported because it is the part worth testing on its own (#677).
export const hostedUiLogoutUrl = (config, clientId) =>
  `https://${config.domain}/logout` +
  `?client_id=${encodeURIComponent(clientId)}` +
  `&logout_uri=${encodeURIComponent(config.redirectSignOut[0])}`

function LoginService() {
  let cachedUserList = {}
  let cachedSession = undefined
  let sessionPromise = undefined

  // refresh the session every 5 mins. v6 has no cognitoUser.refreshSession; forcing a fetch is
  // the equivalent.
  const refreshInterval = 1000 * 60 * 5
  setInterval(async () => {
    try {
      cachedSession = await fetchAuthSession({ forceRefresh: true })
    } catch (err) {
      // not signed in - nothing to refresh
    }
  }, refreshInterval)

  // The Hosted UI settings, or null when this deployment does not offer one.
  const oauthConfig = () => {
    try {
      return authConfig.get().oauth || null
    } catch (err) {
      return null
    }
  }

  const loginService = {
    login: async loginDetails => {
      try {
        // v6 keeps the previous session around during sign-in, so signing in again throws
        // UserAlreadyAuthenticatedException unless the old session is cleared first.
        try {
          await getCurrentUser()
          await signOut()
          cachedSession = undefined
          sessionPromise = undefined
        } catch {
          // not signed in - proceed
        }

        const result = await signIn({
          username: loginDetails.username,
          password: loginDetails.password,
          // The UserMigration trigger only receives the password on the USER_PASSWORD_AUTH flow.
          // Under SRP a user missing from the new pool is just "not found" and never migrates -
          // which is exactly what production did when it switched. The clients allow the flow;
          // A8 removes password sign-in entirely when SSO lands.
          options: { authFlowType: 'USER_PASSWORD_AUTH' },
        })
        // A challenge (a new password, a sign-up code, MFA) is not a session yet. Return the step
        // so the login form can ask for it; fetching the session now would throw.
        if (result && result.nextStep && result.nextStep.signInStep !== 'DONE') {
          return result
        }
        // v6's signIn result carries no tokens; fetch the session so callers (and the login
        // controller) can read the id token payload.
        cachedSession = await fetchAuthSession()
        return result
      } catch (err) {
        console.log('Error signing in user', err)
        return undefined
      }
    },

    // Send the browser to Cognito's Hosted UI (#677). With no provider it shows Cognito's own login
    // page, which is what makes the redirect mechanics testable before a company IdP exists; with a
    // provider it goes straight to that identity provider. Amplify's own OAuth listener completes
    // the flow when the browser comes back with `?code=`, so nothing here handles the return.
    signInWithRedirect: async provider => {
      const { signInWithRedirect } = await import('aws-amplify/auth')

      if (!provider || provider === 'COGNITO') {
        return signInWithRedirect()
      }
      // Remember which provider the session came from (#671): the token does not always carry an
      // `identities` claim - notably when the federated identity is linked to an existing account,
      // where the tokens are the native user's - so the sign-in record would otherwise say
      // "password" for a Microsoft sign-in. Survives the redirect because it is sessionStorage.
      try {
        sessionStorage.setItem('hourglass.signInProvider', String(provider))
      } catch (err) {
        // storage unavailable: the record is a measurement, not a gate
      }

      // A company identity provider is registered by name in Cognito, not one of Amplify's four
      // built-ins, so it goes through `provider.custom`.
      return signInWithRedirect({ provider: { custom: provider } })
    },

    isLoggedIn: async () => {
      try {
        await getCurrentUser()
        return true
      } catch {
        return false
      }
    },

    currentSession: () => {
      // Share a single in-flight lookup. The boot check in app.js and the router guard both call
      // this at startup, and if they each run their own attempt one can win the token-store race
      // while the other exhausts its retries and rejects - which made the boot check redirect a
      // signed-in visitor to /login (the flaky `login` spec under step 24).
      if (cachedSession && cachedSession.tokens && cachedSession.tokens.idToken) {
        return Promise.resolve(cachedSession)
      }

      if (!sessionPromise) {
        sessionPromise = (async () => {
          // fetchAuthSession reads (and refreshes) the token store directly. v6's
          // getCurrentUser() is not a reliable "is there a session" check on a fresh page load, and
          // gating on it here made the first request after a reload go out unauthenticated. It also
          // throws UserUnAuthenticatedException until the token store settles, so retry as a unit
          // (the same reason the Vuex `auth/setUser` action does).
          for (let attempt = 0; attempt < 5; attempt++) {
            try {
              const session = await fetchAuthSession()
              if (session && session.tokens && session.tokens.idToken) {
                cachedSession = session
                return session
              }
            } catch (err) {
              console.log('Error getting current session', err)
            }

            if (attempt < 4) {
              await new Promise(resolve => setTimeout(resolve, 300))
            }
          }

          // Allow a later call to try again (for example after a sign-in).
          sessionPromise = undefined
          return Promise.reject({ error: new Error('No session'), config: {} })
        })()
      }

      return sessionPromise
    },

    currentUser: () => {
      return getCurrentUser()
    },

    // The session is gone, and the app should stop pretending otherwise (#671 step 2b).
    //
    // A rejected request used to route to the login page and leave the session alive, so the next
    // request refreshed the token silently and carried on - which makes a short-lived access token
    // pointless. This ends it for real: the caches here, and Amplify's own token store.
    expireSession: async () => {
      cachedSession = undefined
      sessionPromise = undefined

      try {
        await signOut()
      } catch (err) {
        // A session that is already gone is the state we wanted.
        console.log('Error clearing an expired session', err)
      }
    },

    logout: async () => {
      cachedSession = undefined
      sessionPromise = undefined

      await signOut()

      // Amplify only redirects to the Hosted UI's logout when the session *came from* a redirect
      // (`isOAuthSignIn`). A session started with the password form leaves Cognito's own session
      // cookie alive, so the next "Sign in with..." goes straight back in without asking - which
      // reads as a login screen that does not log out (#677).
      // If this company has an identity provider, arm its logout for the way back from Cognito's
      // own logout - both sessions then end in one gesture (#677).
      armIdpLogout()

      const config = oauthConfig()
      if (config && config.domain && config.redirectSignOut && config.redirectSignOut.length) {
        window.location.assign(hostedUiLogoutUrl(config, authConfig.get().clientId))
        // The page is leaving. The caller must not navigate anywhere else, or it would cancel this
        // one - which is exactly what happened first time: App.vue's `assign('/login')` ran in the
        // same tick and won, leaving Cognito's session alive (#677).
        return true
      }

      return false
    },

    registerUser: async resource => {
      // These used to be Amplify's own validation messages, produced inside Auth.signUp.
      // Checking them here instead costs no round trip to Cognito, so the form says what
      // is wrong immediately - and it means the two invalid-input tests exercise the app
      // rather than the user pool.
      if (!EMAIL_PATTERN.test(resource.email || '')) {
        throw new Error('Invalid email address format.')
      }

      if (!resource.password || resource.password.length < PASSWORD_MINIMUM_LENGTH) {
        throw new Error('Password not long enough')
      }

      // The new pools are admin-create-only, so the public `signUp` no longer works. The backend
      // creates the user with the admin's company and the password the form collected. This is a
      // raw axios call (not the app's configured instance), so attach the verified token itself.
      const session = await loginService.currentSession()
      return axios.post('/api/user', resource, {
        headers: { Authorization: `Bearer ${session.tokens.idToken.toString()}` },
      })
    },

    // The browser can only list a group's users now (the resource-settings typeahead). Granting
    // and revoking roles is admin-only and goes through the backend (#674); the identity-pool
    // role no longer has AdminAddUserToGroup.
    userList: async group => {
      try {
        const session = await fetchAuthSession()

        if (!session || !session.credentials) {
          return { Users: [] }
        }

        // this is surprisingly the only way to tell if the session is active
        if (!session.credentials.expiration) {
          return { Users: [] }
        }

        const config = authConfig.get()
        const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({
          region: config.region,
          credentials: session.credentials,
        })

        const cacheGroup = group || 'sandbox'
        const params = {
          GroupName: group || 'sandbox',
          UserPoolId: config.userPoolId,
          Limit: 60,
        }

        if (!cachedUserList[cacheGroup]) {
          try {
            cachedUserList[cacheGroup] = await cognitoIdentityServiceProvider.listUsersInGroup(params)
          } catch (err) {
            console.error('Error listing users in group', err, err.stack)
          }
        }

        return cachedUserList[cacheGroup]
      } catch (err) {
        console.log('Error gathering user list', err)
        return undefined
      }
    },

    refreshSession: async () => {
      try {
        cachedSession = await fetchAuthSession({ forceRefresh: true })
        return cachedSession
      } catch (err) {
        console.log('Error refreshing session from function', err)
        return undefined
      }
    },

    updateAttributes: async (attributeList, userToChange) => {
      // Amplify expects an object map of attribute -> value, and Cognito accepts all
      // attributes in a single call.
      const attributes = Object.assign({}, ...attributeList)
      await updateUserAttributes({ userAttributes: attributes })
      return userToChange
    },

    forgotPassword: async username => {
      // Deliberately not swallowed. The form shows "a verification code has been sent" when this
      // resolves, so a refusal - UserNotFoundException for an account that does not exist,
      // LimitExceededException for too many attempts - has to reach it. Swallowing it made a refused
      // send look exactly like a delivery problem: the page claimed a code was on its way and
      // nothing had been sent (#671).
      try {
        return await resetPassword({ username })
      } catch (err) {
        console.log('Error in forgot password', err)
        throw err
      }
    },

    verifyPassword: async (username, code, newPassword) => {
      try {
        return await confirmResetPassword({ username, confirmationCode: code, newPassword })
      } catch (err) {
        console.log('Error in verify password', err)
        return undefined
      }
    },

    // Answers the TOTP/SMS/email MFA challenge. The challenge is pending in Amplify, so only the
    // code is needed; throws on failure so the form can show the reason.
    confirmMfaCode: async code => {
      const result = await confirmSignIn({ challengeResponse: code })
      cachedSession = undefined
      sessionPromise = undefined
      return result
    },

    setUpTOTP: () => setUpTOTP(),
    verifyTOTP: code => verifyTOTPSetup({ code }),
    enableTOTP: () => updateMFAPreference({ totp: 'PREFERRED' }),
    disableTOTP: () => updateMFAPreference({ totp: 'DISABLED' }),
    fetchMfa: () => fetchMFAPreference(),

    // Answers CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED. Throws on failure so the login form's
    // catch can show the reason; the challenge is already pending in Amplify, so only the new
    // password is needed here.
    confirmNewPassword: async newPassword => {
      const result = await confirmSignIn({ challengeResponse: newPassword })
      // There was no session while the challenge was pending; drop anything cached before it.
      cachedSession = undefined
      sessionPromise = undefined
      return result
    },

    verifyUser: async (username, code) => {
      try {
        return await confirmSignUp({ username, confirmationCode: code })
      } catch (err) {
        return err
      }
    },

    resendCode: async username => {
      try {
        return await resendSignUpCode({ username })
      } catch (err) {
        return err
      }
    },
  }

  return loginService
}

// Constructed once, here. The Angular factory this replaces was built once by the injector and
// shared, so the module is now that single instance.
export default LoginService()
